/**
 * Raíl de navegación «Despacho» (GEN-PX-002, spec §3.1).
 *
 * Solo pinta lo que ya funciona y lo que el kernel emite en `capabilities.menu`
 * (R-PT-02: el rol lo decide el kernel; ocultar aquí es cosmético, R-PT-04).
 * Orden de la especificación (GEN-PX-E13): Chats · Ficheros | Personas · Agentes |
 * Estrategia · Saber · Normas | Admin; lo que aún no existe no se pinta.
 * GEN-PX-022: mismo orden para todos los perfiles. El kernel emite los ítems propios `personas`,
 * `agentes` y `saber` a todos (el front ya no los deriva de `admin`/`equipo`); lo que cambia por
 * perfil es el contenido de cada vista y `admin`, que sigue siendo solo del administrador.
 * GEN-PX-015: «Normas» (tras Saber) solo sale si el kernel emite algún ítem `mecanismo:*` (hoy, solo al administrador).
 * GEN-PX-016: «Auditoría» va en el último grupo (Admin · Auditoría) y solo sale si el kernel emite el ítem `auditoria` con alguna
 * pestaña para el perfil. Con él, Detenciones deja de ser de Admin; sin él (kernel anterior), sigue en Admin (respaldo, se quita con GEN-PX-021).
 * GEN-PX-017: «Admin» abre la instalación (Herramientas, Modelos, Marca y estilo, Coste, Salud); ya no lista nada que tenga botón propio.
 * Los separadores solo aparecen entre grupos con entradas visibles.
 */
import { Fragment } from 'react'
import { MessageSquare, IdCard, FolderOpen, BookOpen, ShieldCheck, Users, Scale, ClipboardList, Compass } from 'lucide-react'
import { Tooltip } from './ui.jsx'
import { PREFIJO_MECANISMO } from '../mecanismos/registro.js'
import { PESTANAS_ADMIN } from '../lib/admin.js'
import { ID_AUDITORIA, hayAuditoria, itemAuditoria } from '../lib/auditoria.js'

// GEN-PX-017: Admin = la instalación. Sus entradas las emite el kernel solo al admin (ver lib/admin.js); Personas, Agentes, Normas y
// Auditoría tienen botón propio y no resaltan Admin. `detenciones` solo cuenta como de Admin con el kernel anterior (respaldo).
const IDS_ADMIN = ['admin', ...PESTANAS_ADMIN.map(p => p.id), 'detenciones']
// Con Auditoría emitida, Detenciones es de Auditoría y no de Admin (GEN-PX-016).
const esAdmin = (id, conAuditoria = false) => IDS_ADMIN.includes(id) && !(conAuditoria && id === 'detenciones')
// GEN-PX-015: los mecanismos (`mecanismo:*`) ya no son de Admin; los resalta «Normas».
const esNormas = id => id === 'normas' || id.startsWith(PREFIJO_MECANISMO)

/** Iniciales del nombre del tenant (máx. 2) para cuando no hay logo. */
export function inicialesDe(nombre) {
  const p = String(nombre ?? '').trim().split(/\s+/).filter(Boolean)
  if (p.length === 0) return '?'
  return (p.length === 1 ? p[0].slice(0, 2) : p[0][0] + p[1][0]).toUpperCase()
}

/**
 * Entradas del raíl, agrupadas, a partir del menú del kernel.
 * Devuelve [[{clave, etiqueta, Icono, destino, activa}]]: un array por grupo
 * (los grupos vacíos no salen, así que no hay separadores huérfanos).
 */
export function gruposDelRail(menu = [], saberVisible = false) {
  const ok = m => m && m.visible && m.enabled
  const por = id => menu.find(m => m.id === id)

  const grupos = [
    [
      ok(por('chats')) && { clave: 'chats', etiqueta: 'Chats', Icono: MessageSquare, destino: 'chat', activa: v => v === 'chat' },
      ok(por('ficheros')) && { clave: 'ficheros', etiqueta: 'Ficheros', Icono: FolderOpen, destino: 'ficheros', activa: v => v === 'ficheros' },
    ],
    [
      // RESPALDO TEMPORAL (kernel anterior a GEN-PX-022, que aún no emite `personas`, `agentes` ni `saber`): el Pórtico se
      // despliega al fusionar y el kernel espera al pipeline. Se quita cuando el kernel de PX-022 esté desplegado,
      // junto con la retirada del panel derecho (GEN-PX-021). Con el ítem propio, manda el ítem.
      (ok(por('personas')) || ok(por('admin'))) && { clave: 'personas', etiqueta: 'Personas', Icono: Users, destino: 'personas', activa: v => v === 'personas' },
      (ok(por('agentes')) || ok(por('equipo'))) && { clave: 'agentes', etiqueta: 'Agentes', Icono: IdCard, destino: 'agentes', activa: v => v === 'agentes' || v === 'equipo' || v === 'agentes-permisos' },
    ],
    [
      // GEN-PX-018: Estrategia, para todos los perfiles; solo sale si el kernel emite el ítem `estrategia`.
      ok(por('estrategia')) && { clave: 'estrategia', etiqueta: 'Estrategia', Icono: Compass, destino: 'estrategia', activa: v => v === 'estrategia' },
      // GEN-EKB-018: zona propia. GEN-PX-022: el kernel la emite a todos; quien no tiene KBs ve un estado vacío.
      // Respaldo (mismo motivo): sin ítem `saber` en el menú, vale la regla anterior (GET /v1/ekb/saber → visible).
      (por('saber') ? ok(por('saber')) : saberVisible) && { clave: 'saber', etiqueta: 'Saber', Icono: BookOpen, destino: 'saber', activa: v => v === 'saber' },
      menu.some(m => ok(m) && m.id.startsWith(PREFIJO_MECANISMO)) && { clave: 'normas', etiqueta: 'Normas', Icono: Scale, destino: 'normas', activa: esNormas },
    ],
    [
      ok(por('admin')) && { clave: 'admin', etiqueta: 'Admin', Icono: ShieldCheck, destino: 'admin', activa: v => esAdmin(v, hayAuditoria(menu)) },
      itemAuditoria(menu) && { clave: ID_AUDITORIA, etiqueta: 'Auditoría', Icono: ClipboardList, destino: ID_AUDITORIA, activa: v => v === ID_AUDITORIA || (v === 'detenciones' && hayAuditoria(menu)) },
    ],
  ]
  return grupos.map(g => g.filter(Boolean)).filter(g => g.length > 0)
}

export default function Rail({ tenant, menu, saberVisible = false, activeView, onNavigate, onHome }) {
  const grupos = gruposDelRail(menu, saberVisible)
  return (
    <nav className="dk-rail" aria-label="Navegación principal">
      <Tooltip texto={tenant.nombre} lado="derecha">
        <button type="button" className="dk-rail__logo" onClick={onHome} aria-label={`Ir al inicio de ${tenant.nombre}`}>
          {tenant.logo ? <img src={tenant.logo} alt="" /> : inicialesDe(tenant.nombre)}
        </button>
      </Tooltip>
      {grupos.map((g, i) => (
        <Fragment key={g[0].clave}>
          {i > 0 && <span className="dk-rail__filete" role="separator" />}
          {g.map(({ clave, etiqueta, Icono, destino, activa }) => {
            const on = activa(activeView)
            return (
              <Tooltip key={clave} texto={etiqueta} lado="derecha">
                <button
                  type="button"
                  className={'dk-rail__item' + (on ? ' dk-rail__item--activa' : '')}
                  aria-current={on ? 'page' : undefined}
                  onClick={() => onNavigate(destino)}
                >
                  <Icono aria-hidden="true" strokeWidth={1.8} />
                  {etiqueta}
                </button>
              </Tooltip>
            )
          })}
        </Fragment>
      ))}
    </nav>
  )
}
